import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import i18n, { normalizeLanguage } from "@/i18n";
import type { User, Session } from "@supabase/supabase-js";

interface AuthCtx {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthCtx>({ user: null, session: null, loading: true, signOut: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Load user language preference from profile
  const loadLanguagePreference = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("language_preference")
      .eq("id", userId)
      .single();
    const preferredLanguage = normalizeLanguage(data?.language_preference) ?? normalizeLanguage(localStorage.getItem("ui_language"));
    if (preferredLanguage) {
      i18n.changeLanguage(preferredLanguage);
      localStorage.setItem("ui_language", preferredLanguage);
    }
  };

  const enforceAccountAccess = async (userId: string) => {
    const { data, error } = await supabase
      .from("user_account_status")
      .select("status")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) {
      console.warn("Could not verify account status", error);
      return;
    }
    if (data?.status === "disabled" || data?.status === "suspended") {
      await supabase.auth.signOut();
    }
  };

  const hydrateAuthenticatedUser = (userId: string) => {
    loadLanguagePreference(userId);
    enforceAccountAccess(userId);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        setTimeout(() => hydrateAuthenticatedUser(session.user.id), 0);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        hydrateAuthenticatedUser(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
